/**
 * STAGE 4 腐叶·森林 —— 曲线弹 + 滞留孢子弹 + 藤蔓封位。
 * 小兵射型：孢子囊（deathcap）/ 滞留孢子 3 路（spore）/ 曲线 3 路（lotus）/ 环 + 狙（drake）。
 */
import * as P from '../../game/patterns.js';
import { aimOf, fireTimer, curveBullet, sporeBullet, burstBullet, s4Mid, s4Big } from './bosses.js';

const DROP_STD = [{ type: 'P', weight: 2 }, { type: 'POINT', weight: 3 }]; // P 占比 40%
const DROP_POINT = [{ type: 'POINT', weight: 1 }];
const DROP_ELITE = [{ type: 'B', weight: 1 }, { type: 'STAR', weight: 1 }, { type: 'P', weight: 2 }]; // 精英必掉半数为 P

/** 毒菇：直线 + 单狙 */
function deathcap(x, y, vx, vy) {
  return {
    x, y, hp: 14, r: 12, spriteKey: 's4_deathcap', scoreValue: 250, drops: DROP_STD,
    script(e, dt, ctx) {
      e.x += vx * dt * 60;
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 340 && fireTimer(e, dt, 1.8)) {
        // 孢子囊：慢速绿囊飞出 0.9s 后爆裂成 3 颗小孢子（囊→飞→裂→孢）
        P.aimed(ctx.field, e.x, e.y, e.age, aimOf(ctx, e.x, e.y), { n: 1, speed: 1.4, r: 7, color: 'orb_green', jitter: 0.05, onUpdate: burstBullet(ctx.field, 0.9) });
      }
    },
  };
}

/** 孢子精：正弦 + 滞留孢子 3 路 */
function spore(x, y, vy) {
  return {
    x, y, hp: 10, r: 11, spriteKey: 's4_spore', scoreValue: 300, drops: DROP_POINT,
    script(e, dt, ctx) {
      e.x += Math.sin(e.age * 1.6) * 0.8 * dt * 60;
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 320 && fireTimer(e, dt, 2.4)) {
        const aim = aimOf(ctx, e.x, e.y);
        for (const k of [-1, 0, 1]) {
          ctx.field.spawn({
            x: e.x, y: e.y, speed: 1.5, angle: aim + k * 0.3, r: 5,
            color: 'orb_green', onUpdate: sporeBullet(1.2),
          });
        }
      }
    },
  };
}

/** 莲花妖：纵队 + 曲线 3 路 */
function lotus(x, y, vy) {
  return {
    x, y, hp: 18, r: 13, spriteKey: 's4_lotus', scoreValue: 350, drops: DROP_STD,
    script(e, dt, ctx) {
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 340 && fireTimer(e, dt, 2.0)) {
        const aim = aimOf(ctx, e.x, e.y);
        for (const k of [-1, 0, 1]) {
          ctx.field.spawn({
            x: e.x, y: e.y, speed: 1.9, angle: aim + k * 0.25, r: 4,
            color: 'drop_pink', onUpdate: curveBullet(k * 1.0),
          });
        }
      }
    },
  };
}

/** 林龙精英：悬停环 + 3 连狙 */
function drake(x) {
  return {
    x, y: -28, hp: 28, r: 16, spriteKey: 's4_drake', scoreValue: 600, drops: DROP_ELITE,
    script(e, dt, ctx) {
      if (e.y < 140) {
        e.y += 1.6 * dt * 60;
      } else if ((e._hold = (e._hold || 0) + dt) < 3.5) {
        e.x += Math.sin(e.age * 1.3) * 0.6 * dt * 60;
        if (fireTimer(e, dt, 2.0)) {
          P.ring(ctx.field, e.x, e.y, e.age, 0, { n: 10, speed: 1.7, offset: e.age * 0.5, color: 'orb_yellow' });
        }
        if (fireTimer(e, dt, 1.3, '_ft2')) {
          P.aimed(ctx.field, e.x, e.y, e.age, aimOf(ctx, e.x, e.y), { n: 3, speed: 2.4, color: 'drop_pink', jitter: 0.05 });
        }
      } else {
        e.y += 2.2 * dt * 60;
      }
    },
  };
}

export default {
  id: 4, name: '腐叶·森林', bg: 'bg4', bgm: 'stage4',
  *script(api) {
    const W = api.VIEW_W;
    yield { say: 'STAGE 4  腐叶·森林' };
    yield { wait: 1.0 };

    // W1 毒菇纵队
    yield { wave: [0, 1, 2, 3].map((i) => deathcap(W / 2, -24 - i * 44, 0, 1.6)) };
    yield { wait: 4.0 };
    // W2 孢子精横排
    yield { wave: [0, 1, 2, 3].map((i) => spore(80 + i * 105, -24, 1.0)) };
    yield { wait: 4.5 };
    // W3 莲花交叉
    yield {
      wave: [
        ...[0, 1].map((i) => lotus(100 + i * 60, -24 - i * 44, 1.4)),
        ...[0, 1].map((i) => lotus(W - 100 - i * 60, -24 - i * 44, 1.4)),
        deathcap(W / 2, -60, 0, 1.8),
      ],
    };
    yield { wait: 4.0 };
    // W4 精英林龙
    yield { wave: [drake(W / 2)] };
    yield { clear: true };
    yield { wait: 1.0 };

    yield { boss: s4Mid() };
    yield { wait: 2.0 };

    // W5 包夹（道中二，强度上升）
    yield {
      wave: [
        ...[0, 1, 2].map((i) => deathcap(-20, 80 + i * 44, 1.5, 0.5)),
        ...[0, 1, 2].map((i) => deathcap(W + 20, 100 + i * 44, -1.5, 0.5)),
      ],
    };
    yield { wait: 4.5 };
    // W6 孢子 + 莲花混编
    yield {
      wave: [
        ...[0, 1, 2].map((i) => spore(90 + i * 150, -24, 1.1)),
        ...[0, 1].map((i) => lotus(140 + i * 200, -60, 1.3)),
      ],
    };
    yield { wait: 4.0 };
    // W7 双林龙
    yield { wave: [drake(140), drake(W - 140)] };
    yield { clear: true };
    yield { wait: 1.0 };

    yield { boss: s4Big() };
    yield { say: 'STAGE 4 CLEAR' };
  },
};
